import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Coins, Zap, Mic, Globe, FileText, Clock, Users, 
  ArrowRight, Check, Shield, Sparkles, Calculator,
  ChevronRight, BadgeCheck, TrendingUp, Wallet
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatAi = (amount) => `${amount} ${amount === 1 ? "AI" : "AIs"}`;

export default function Aipps() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [balance, setBalance] = useState(0);
  const [pricing, setPricing] = useState(null);

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchPackages();
    fetchPricing();
    if (auth.user) {
      fetchBalance();
    }
  }, [auth.user]);

  // Check for payment return
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchPackages = async () => {
    try {
      const res = await axios.get(`${API}/credits/packages`);
      setPackages(res.data);
    } catch (err) {
      console.error("Failed to load packages");
    }
  };

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/credits/pricing`);
      setPricing(res.data);
    } catch (err) {
      console.error("Failed to load pricing");
    }
  };

  const fetchBalance = async () => {
    try {
      const res = await axios.get(`${API}/credits/balance`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setBalance(res.data.balance);
    } catch (err) {
      console.error("Failed to load balance");
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 10) {
      toast.error("Payment verification timed out. Please check your account.");
      return;
    }

    try {
      const res = await axios.get(`${API}/credits/checkout/status/${sessionId}`, {
        headers: getHeaders(),
        withCredentials: true
      });

      if (res.data.payment_status === "paid") {
        toast.success(`Payment successful! ${formatAi(res.data.credits)} added to your account.`);
        setBalance(res.data.balance);
        // Clear URL params
        navigate("/aipps", { replace: true });
        return;
      }

      if (res.data.status === "expired") {
        toast.error("Payment session expired.");
        navigate("/aipps", { replace: true });
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (err) {
      console.error("Payment status check failed");
    }
  };

  const handlePurchase = async () => {
    if (!auth.user) {
      toast.error("Please login to purchase AI");
      navigate("/");
      return;
    }

    if (!selectedPackage && !customAmount) {
      toast.error("Please select a package or enter a custom amount");
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        origin_url: window.location.origin,
      };

      if (selectedPackage) {
        payload.package_id = selectedPackage;
      } else {
        payload.custom_amount = parseFloat(customAmount);
      }

      const res = await axios.post(`${API}/credits/checkout`, payload, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });

      // Redirect to Stripe
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initiate payment");
      setIsLoading(false);
    }
  };

  const benefits = [
    {
      icon: FileText,
      title: "No Secretary Needed",
      description: "MeetAI automatically generates professional meeting minutes. Save thousands annually on secretarial costs."
    },
    {
      icon: Globe,
      title: "Affordable Interpretation",
      description: "Real-time translation at a fraction of interpreter costs."
    },
    {
      icon: Mic,
      title: "AI Transcription",
      description: "Accurate transcription with speaker identification."
    },
    {
      icon: Clock,
      title: "Free Meeting Start",
      description: "Start any meeting for free."
    }
  ];

  const comparisons = [
    { service: "Professional Secretary", traditional: "High monthly cost", aipps: "AI credits", savings: "99%" },
    { service: "Human Interpreter", traditional: "High hourly cost", aipps: "AI credits", savings: "95%" },
    { service: "Transcription Service", traditional: "High hourly cost", aipps: "AI credits", savings: "85%" },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 via-slate-950 to-green-500/10" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-6 py-20">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-16">
            <button 
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
            >
              <Zap className="w-6 h-6 text-red-500" />
              <span className="font-['Barlow_Condensed'] text-xl font-bold">AllBaise</span>
            </button>

            {auth.user && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 bg-emerald-500/20 px-4 py-2 rounded-full border border-emerald-500/30">
                  <Coins className="w-5 h-5 text-emerald-400" />
              <span className="font-semibold text-emerald-400">{formatAi(balance)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Hero Content */}
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full mb-8">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-emerald-300">AI-Powered Meeting Intelligence</span>
            </div>
            
            <h1 className="font-['Barlow_Condensed'] text-5xl md:text-7xl font-bold mb-6">
              <span className="text-white">BUY </span>
              <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">AI</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-400 mb-8 max-w-2xl mx-auto">
              Power your meetings with AI. 
              <span className="text-white"> No secretary. No expensive interpreters.</span> 
              <br />Just intelligent automation.
            </p>
            
            <div className="flex items-center justify-center gap-4 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <BadgeCheck className="w-4 h-4 text-emerald-500" />
                1 AI credit
              </span>
              <span className="flex items-center gap-1">
                <Shield className="w-4 h-4 text-emerald-500" />
                Secure Payments
              </span>
              <span className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                Save up to 99%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <section className="py-20 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-center mb-4">
            WHY <span className="text-emerald-400">AI</span>?
          </h2>
            <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            Transform your meetings with AI-powered intelligence
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, idx) => (
              <div 
                key={idx}
                className="p-6 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:border-emerald-500/30 transition-all group"
              >
                <div className="w-12 h-12 mb-4 flex items-center justify-center bg-emerald-500/20 rounded-lg group-hover:bg-emerald-500/30 transition-colors">
                  <benefit.icon className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-sm text-slate-400">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-['Barlow_Condensed'] text-3xl font-bold text-center mb-4">
            COMPARE <span className="text-emerald-400">SAVINGS</span>
          </h2>
            <p className="text-slate-400 text-center mb-12">See how much you save with AI vs traditional services</p>
          
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
            <div className="grid grid-cols-4 gap-4 p-4 bg-slate-800 text-sm font-medium text-slate-400">
              <div>Service</div>
              <div>Traditional Cost</div>
              <div>With AI</div>
              <div>Savings</div>
            </div>
            {comparisons.map((item, idx) => (
              <div key={idx} className="grid grid-cols-4 gap-4 p-4 border-t border-slate-700/50 items-center">
                <div className="text-white font-medium">{item.service}</div>
                <div className="text-red-400 line-through">{item.traditional}</div>
                <div className="text-emerald-400 font-semibold">{item.aipps}</div>
                <div className="inline-flex items-center gap-1 text-emerald-400">
                  <TrendingUp className="w-4 h-4" />
                  {item.savings}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Packages */}
      <section className="py-20 bg-slate-900/50" id="packages">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-['Barlow_Condensed'] text-3xl font-bold text-center mb-4">
            CHOOSE YOUR <span className="text-emerald-400">PACKAGE</span>
          </h2>
          <p className="text-slate-400 text-center mb-12">Select a package or enter a custom amount</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                onClick={() => {
                  setSelectedPackage(pkg.id);
                  setCustomAmount("");
                }}
                className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all ${
                  selectedPackage === pkg.id
                    ? "border-emerald-500 bg-emerald-500/10"
                    : "border-slate-700 bg-slate-800/50 hover:border-slate-600"
                }`}
                data-testid={`package-${pkg.id}`}
              >
                {pkg.bonus > 0 && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-emerald-500 text-xs font-bold rounded-full">
                    +{pkg.bonus} BONUS
                  </div>
                )}
                
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white capitalize mb-2">
                  {pkg.id}
                </h3>
                
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-3xl font-bold text-emerald-400">{pkg.total_aipps}</span>
                  <span className="text-slate-400">AI</span>
                </div>
                
                <div className="text-slate-400 text-sm mb-4">
                  AI credits
                </div>
                
                {selectedPackage === pkg.id && (
                  <div className="flex items-center justify-center gap-1 text-emerald-400 text-sm">
                    <Check className="w-4 h-4" />
                    Selected
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Custom Amount */}
          <div className="max-w-md mx-auto mb-8">
            <div className="text-center text-slate-400 text-sm mb-4">Or enter a custom amount</div>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">R</span>
                <input
                  type="number"
                  min="10"
                  placeholder="Enter amount"
                  value={customAmount}
                  onChange={(e) => {
                    setCustomAmount(e.target.value);
                    setSelectedPackage(null);
                  }}
                  className="w-full pl-8 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-emerald-500 focus:outline-none"
                  data-testid="custom-amount-input"
                />
              </div>
              <span className="flex flex-col items-center justify-center px-4 bg-slate-800 border border-slate-700 rounded-lg text-slate-400">
                  <span>= {customAmount ? formatAi(parseInt(customAmount)) : "0 AIs"}</span>
              </span>
            </div>
          </div>
          
          {/* Purchase Button */}
          <div className="text-center">
            <Button
              onClick={handlePurchase}
              disabled={isLoading || (!selectedPackage && !customAmount)}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-12 py-6 text-lg rounded-full shadow-lg shadow-emerald-500/30 disabled:opacity-50"
              data-testid="buy-aipps-btn"
            >
              {isLoading ? (
                "Processing..."
              ) : (
                <>
                  <Wallet className="w-5 h-5 mr-2" />
                  Buy AI Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
            
            {!auth.user && (
              <p className="text-slate-500 text-sm mt-4">
                Please <button onClick={() => navigate("/")} className="text-emerald-400 hover:underline">login</button> to purchase AI
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Service Pricing Details */}
      {pricing && (
        <section className="py-20">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="font-['Barlow_Condensed'] text-3xl font-bold text-center mb-4">
              SERVICE <span className="text-emerald-400">PRICING</span>
            </h2>
            <p className="text-center text-slate-500 text-sm mb-12">AI credits pricing</p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 bg-slate-800/50 border border-slate-700/50 rounded-xl">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-6 h-6 text-violet-400" />
                  <h3 className="font-semibold text-white">Meetings</h3>
                </div>
                <p className="text-slate-400 text-sm mb-4">{pricing.meeting.description}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Free period:</span>
                    <span className="text-emerald-400">{pricing.meeting.free_minutes} minutes</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">After free period:</span>
                    <span className="text-white">
                      {pricing.meeting.cost_after_free} AI
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="p-6 bg-slate-800/50 border border-slate-700/50 rounded-xl">
                <div className="flex items-center gap-3 mb-4">
                  <Mic className="w-6 h-6 text-blue-400" />
                  <h3 className="font-semibold text-white">AI Notetaker</h3>
                </div>
                <p className="text-slate-400 text-sm mb-4">{pricing.notetaker.description}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Free period:</span>
                    <span className="text-emerald-400">{pricing.notetaker.free_minutes} minutes</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">After free period:</span>
                    <span className="text-white">
                      {pricing.notetaker.cost_per_15min} AI/15min
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="p-6 bg-slate-800/50 border border-slate-700/50 rounded-xl">
                <div className="flex items-center gap-3 mb-4">
                  <Globe className="w-6 h-6 text-amber-400" />
                  <h3 className="font-semibold text-white">Interpreter</h3>
                </div>
                <p className="text-slate-400 text-sm mb-4">{pricing.interpreter.description}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Cost per language:</span>
                    <span className="text-white">
                      {pricing.interpreter.cost_per_language} AI/30min
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="p-6 bg-slate-800/50 border border-slate-700/50 rounded-xl">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="w-6 h-6 text-rose-400" />
                  <h3 className="font-semibold text-white">MeetAI Minutes</h3>
                </div>
                <p className="text-slate-400 text-sm mb-4">{pricing.meetai_minutes.description}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Draft minutes:</span>
                    <span className="text-white">
                      {pricing.meetai_minutes.draft_cost} AI
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Final minutes:</span>
                    <span className="text-white">
                      {pricing.meetai_minutes.final_cost} AI
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-slate-800 py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Zap className="w-6 h-6 text-red-500" />
            <span className="font-['Barlow_Condensed'] text-xl font-bold">AllBaise</span>
          </div>
          <p className="text-slate-500 text-sm">
            Closing workspace gaps with Innovation and AI
          </p>
        </div>
      </footer>
    </div>
  );
}
